import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { UnsavedChangesDialogService } from '../../../services/unsaved-changes-dialog.service';

@Component({
  selector: 'app-unsaved-changes-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule],
  template: `
    <p-dialog
      [visible]="dialog.visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      styleClass="kansei-dialog unsaved-changes-dialog"
      header="Unsaved Changes"
      (onHide)="onHide()"
    >
      <div class="unsaved-body">
        <div class="unsaved-icon" aria-hidden="true">
          <i class="ri-error-warning-line"></i>
        </div>
        <div class="unsaved-copy">
          <p class="unsaved-title">You have unsaved changes on this page.</p>
          <p class="unsaved-message">Save the current draft before leaving, or leave without saving these changes.</p>
        </div>
      </div>

      <div class="modal-footer unsaved-actions">
        <button type="button" class="modal-btn-cancel unsaved-stay-btn" (click)="stay()">Stay</button>
        <button type="button" class="modal-btn-cancel" (click)="discard()">Leave Without Saving</button>
        <button type="button" class="modal-btn-primary" (click)="save()">Save Draft and Leave</button>
      </div>
    </p-dialog>
  `,
  styles: [`
    :host ::ng-deep .unsaved-changes-dialog {
      width: min(560px, calc(100vw - 32px)) !important;
    }

    :host ::ng-deep .unsaved-changes-dialog .p-dialog-content {
      padding: 0 !important;
    }

    :host ::ng-deep .unsaved-changes-dialog .p-dialog-header {
      background: linear-gradient(135deg, #062847 0%, #0c3b62 100%) !important;
      border-bottom: 0 !important;
      color: #fff !important;
      padding: 18px 20px !important;
    }

    :host ::ng-deep .unsaved-changes-dialog .p-dialog-title {
      color: #fff !important;
      font-size: 20px !important;
      font-weight: 700 !important;
    }

    :host ::ng-deep .unsaved-changes-dialog .p-dialog-header-icon,
    :host ::ng-deep .unsaved-changes-dialog .p-dialog-close-button {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    :host ::ng-deep .unsaved-changes-dialog .p-dialog-header-icon svg,
    :host ::ng-deep .unsaved-changes-dialog .p-dialog-close-button svg {
      color: rgba(255, 255, 255, 0.8) !important;
      fill: rgba(255, 255, 255, 0.8) !important;
    }

    :host ::ng-deep .unsaved-changes-dialog .p-dialog-header-icon:hover,
    :host ::ng-deep .unsaved-changes-dialog .p-dialog-close-button:hover {
      background: rgba(255, 255, 255, 0.12) !important;
      color: #fff !important;
    }

    .unsaved-body {
      align-items: flex-start;
      display: grid;
      gap: 14px;
      grid-template-columns: 42px 1fr;
      padding: 22px 24px 20px;
    }

    .unsaved-icon {
      align-items: center;
      background: #fff3cd;
      border: 1px solid #ffe39a;
      border-radius: 50%;
      color: #a66600;
      display: inline-flex;
      font-size: 22px;
      height: 42px;
      justify-content: center;
      width: 42px;
    }

    .unsaved-copy {
      min-width: 0;
    }

    .unsaved-title {
      color: var(--color-text-primary);
      font-size: 16px;
      font-weight: 700;
      line-height: 1.35;
      margin: 0 0 6px;
    }

    .unsaved-message {
      color: var(--color-text-secondary);
      font-size: 14px;
      line-height: 1.5;
      margin: 0;
    }

    .unsaved-actions {
      padding: 16px 24px !important;
    }

    .unsaved-actions button {
      height: 42px;
      font-size: 14px;
      padding-left: 18px;
      padding-right: 18px;
      white-space: nowrap;
    }

    .unsaved-stay-btn {
      background: var(--color-bg-surface-muted);
      border-color: transparent;
      min-width: 78px;
    }

    @media (max-width: 560px) {
      .unsaved-body {
        grid-template-columns: 1fr;
      }

      .unsaved-actions {
        align-items: stretch;
        flex-direction: column-reverse;
      }

      .unsaved-actions button {
        width: 100%;
      }
    }
  `]
})
export class UnsavedChangesDialogComponent {
  readonly dialog = inject(UnsavedChangesDialogService);

  onHide(): void {
    if (this.dialog.isWaitingForDecision()) {
      this.dialog.choose('stay');
    }
  }

  stay(): void {
    this.dialog.choose('stay');
  }

  discard(): void {
    this.dialog.choose('discard');
  }

  save(): void {
    this.dialog.choose('save');
  }
}
